import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import { useLearning } from './LearningContext';

/**
 * Mission Center / Mission Runner data layer (design spec §9.3, §10.3).
 *
 * Deliberately NOT built on `api/client.js`: that wrapper collapses every
 * failure into `new Error(message)`, which loses the HTTP status and the
 * `code` from the mission router's `{error, code}` envelope. The Mission
 * Center has to tell "session expired" from "no permission" from "offline"
 * from "this mission has no runnable steps" (spec §11), so the transport keeps
 * both.
 *
 * The provider owns three things:
 *   1. the Mission Center list + due counts + client-side filters,
 *   2. the runner's request surface (start / submit step / complete),
 *   3. the crash-safe draft of an in-flight attempt (spec §11.1) — a reload or
 *      a dropped connection must never cost a typed answer or a confidence
 *      rating.
 */

const MissionContext = createContext(null);

/** localStorage prefix for in-flight attempts. Spec §11.1 + storage-key contract. */
export const MISSION_DRAFT_PREFIX = 'etch.mission.';

/** Drafts older than this are pruned on load — an abandoned run is not a draft. */
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** The five steps, in order. `feedback` is produced by the engine, never submitted. */
export const MISSION_STEPS = ['recall', 'apply', 'cite', 'confidence', 'feedback'];
export const SUBMITTABLE_STEPS = ['recall', 'apply', 'cite', 'confidence'];

/** Mission origins the backend accepts (mission-engine.js `MISSION_ORIGINS`). */
export const MISSION_ORIGINS = ['einpraegen', 'onboarding', 'challenge', 'generated'];

/** Mastery ladder, low → high (mastery.js `MASTERY_CONSTANTS.LEVELS`). */
export const MASTERY_LEVELS = ['new', 'seen', 'practiced', 'secure'];

/** Rough run length per authored difficulty (1..5), in minutes. */
const MINUTES_BY_DIFFICULTY = { 1: 3, 2: 5, 3: 7, 4: 10, 5: 12 };

export function estimatedMinutes(mission) {
  const difficulty = Number(mission?.difficulty);
  if (!Number.isFinite(difficulty)) return MINUTES_BY_DIFFICULTY[2];
  return MINUTES_BY_DIFFICULTY[Math.min(5, Math.max(1, Math.round(difficulty)))] ?? MINUTES_BY_DIFFICULTY[2];
}

/** Error carrying the HTTP status and the router's machine-readable `code`. */
export class MissionApiError extends Error {
  constructor(message, status = 0, code = 'internal_error') {
    super(message);
    this.name = 'MissionApiError';
    this.status = status;
    this.code = code;
  }
}

function codeFromStatus(status) {
  if (status === 401) return 'session_expired';
  if (status === 403) return 'permission_denied';
  if (status === 402) return 'app_not_licensed';
  if (status === 423) return 'tenant_suspended';
  return 'internal_error';
}

/**
 * i18n key for an error, with a generic fallback. Returns an ARRAY so
 * `t(...)` falls through to `states.error` when the specific key is absent —
 * a missing translation must not render a raw backend string at the user.
 */
export function missionErrorKeys(error) {
  const code = error?.code || 'internal_error';
  if (code === 'network_error') return ['states.offline'];
  if (code === 'session_expired') return ['states.sessionExpired'];
  if (code === 'permission_denied' || code === 'app_not_licensed' || code === 'tenant_suspended') {
    return ['states.permissionDenied'];
  }
  return [`missions.error.${code}`, 'states.error'];
}

/* -------------------------------------------------------------------------- */
/* Draft persistence (spec §11.1)                                             */
/* -------------------------------------------------------------------------- */

function safeStorage() {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage;
  } catch {
    // Safari private mode / disabled storage — the runner still works, it just
    // cannot survive a reload. Never let this throw into the render path.
    return null;
  }
}

export function readDraft(attemptId) {
  const store = safeStorage();
  if (!store || !attemptId) return null;
  try {
    const raw = store.getItem(`${MISSION_DRAFT_PREFIX}${attemptId}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function writeDraft(draft) {
  const store = safeStorage();
  if (!store || !draft?.attemptId) return;
  try {
    store.setItem(
      `${MISSION_DRAFT_PREFIX}${draft.attemptId}`,
      JSON.stringify({ ...draft, savedAt: Date.now() })
    );
  } catch {
    /* quota exceeded — the in-memory state is still authoritative */
  }
}

export function clearDraft(attemptId) {
  const store = safeStorage();
  if (!store || !attemptId) return;
  try {
    store.removeItem(`${MISSION_DRAFT_PREFIX}${attemptId}`);
  } catch {
    /* ignore */
  }
}

/** Every stored draft, newest first, with expired ones pruned as a side effect. */
export function listDrafts() {
  const store = safeStorage();
  if (!store) return [];
  const drafts = [];
  const expired = [];
  for (let i = 0; i < store.length; i += 1) {
    const key = store.key(i);
    if (!key || !key.startsWith(MISSION_DRAFT_PREFIX)) continue;
    try {
      const draft = JSON.parse(store.getItem(key));
      if (!draft?.attemptId) {
        expired.push(key);
      } else if (Date.now() - (draft.savedAt ?? 0) > DRAFT_TTL_MS) {
        expired.push(key);
      } else {
        drafts.push(draft);
      }
    } catch {
      expired.push(key);
    }
  }
  for (const key of expired) {
    try {
      store.removeItem(key);
    } catch {
      /* ignore */
    }
  }
  return drafts.sort((a, b) => (b.savedAt ?? 0) - (a.savedAt ?? 0));
}

/** The newest unfinished draft for a mission, or null. */
export function findDraftForMission(missionId) {
  if (!missionId) return null;
  return listDrafts().find((draft) => draft.missionId === missionId && !draft.completed) ?? null;
}

/* -------------------------------------------------------------------------- */
/* Status derivation                                                          */
/* -------------------------------------------------------------------------- */

/**
 * The status badge on a MissionCard (spec §10.3): due / new / in progress /
 * secure. Deliberately derived here rather than in the card so the Mission
 * Center and the runner agree on one definition.
 */
export function deriveMissionStatus(mission, { due = false, hasDraft = false } = {}) {
  if (hasDraft) return 'progress';
  if (mission?.mastery?.level === 'secure') return 'secure';
  if (due) return 'due';
  if ((mission?.mastery?.attempts ?? 0) > 0) return 'progress';
  return 'new';
}

/* -------------------------------------------------------------------------- */
/* Provider                                                                   */
/* -------------------------------------------------------------------------- */

export function MissionProvider({ children }) {
  const { token } = useAuth();
  const { effectiveWorkspace } = useLearning();

  const [missions, setMissions] = useState([]);
  const [dueIds, setDueIds] = useState(() => new Set());
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ origin: 'all', topic: 'all' });
  const [offline, setOffline] = useState(
    () => typeof navigator !== 'undefined' && navigator.onLine === false
  );

  const tokenRef = useRef(token);
  tokenRef.current = token;

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  const request = useCallback(async (path, { method = 'GET', body, signal } = {}) => {
    const authToken =
      tokenRef.current ?? (safeStorage() ? safeStorage().getItem('etch.token') : null);
    let response;
    try {
      response = await fetch(`/api/missions${path}`, {
        method,
        signal,
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
    } catch (err) {
      if (err?.name === 'AbortError') throw err;
      throw new MissionApiError(err?.message || 'network error', 0, 'network_error');
    }

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new MissionApiError(
        payload.error || `HTTP ${response.status}`,
        response.status,
        payload.code || codeFromStatus(response.status)
      );
    }
    return payload;
  }, []);

  const workspaceQuery = useMemo(
    () => (effectiveWorkspace ? `workspace=${encodeURIComponent(effectiveWorkspace)}` : ''),
    [effectiveWorkspace]
  );

  /** Mission Center payload: the full list plus the due subset and its counts. */
  const refresh = useCallback(
    async ({ signal } = {}) => {
      setLoading(true);
      setError(null);
      try {
        const listQuery = ['status=all', 'limit=200', workspaceQuery].filter(Boolean).join('&');
        const dueQuery = ['limit=100', workspaceQuery].filter(Boolean).join('&');
        const [list, due] = await Promise.all([
          request(`?${listQuery}`, { signal }),
          request(`/due?${dueQuery}`, { signal }),
        ]);
        setMissions(Array.isArray(list.missions) ? list.missions : []);
        setDueIds(new Set((due.missions || []).map((m) => m.id)));
        setCounts(due.counts ?? null);
      } catch (err) {
        if (err?.name === 'AbortError') return;
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    [request, workspaceQuery]
  );

  useEffect(() => {
    const controller = new AbortController();
    refresh({ signal: controller.signal });
    return () => controller.abort();
  }, [refresh]);

  /* -- Runner surface ---------------------------------------------------- */

  const getMission = useCallback(
    (missionId, attemptId) =>
      request(`/${missionId}${attemptId ? `?attemptId=${encodeURIComponent(attemptId)}` : ''}`),
    [request]
  );

  const startAttempt = useCallback(
    (missionId) => request(`/${missionId}/attempts`, { method: 'POST', body: {} }),
    [request]
  );

  const submitStep = useCallback(
    (missionId, attemptId, step, payload) =>
      request(`/${missionId}/attempts/${attemptId}/steps`, {
        method: 'POST',
        body: { step, payload },
      }),
    [request]
  );

  const completeAttempt = useCallback(
    (missionId, attemptId, durationMs) =>
      request(`/${missionId}/attempts/${attemptId}/complete`, {
        method: 'POST',
        body: { durationMs: durationMs ?? null },
      }),
    [request]
  );

  const fetchMastery = useCallback(
    (days = 30, options = {}) =>
      request(`/mastery?${['days=' + days, workspaceQuery].filter(Boolean).join('&')}`, options),
    [request, workspaceQuery]
  );

  /* -- Derived Mission Center data --------------------------------------- */

  const topics = useMemo(() => {
    const seen = new Set();
    for (const mission of missions) {
      if (mission.topic) seen.add(mission.topic);
    }
    return [...seen].sort((a, b) => a.localeCompare(b));
  }, [missions]);

  const filtered = useMemo(
    () =>
      missions.filter((mission) => {
        if (filters.origin !== 'all' && mission.origin !== filters.origin) return false;
        if (filters.topic !== 'all' && (mission.topic ?? '') !== filters.topic) return false;
        return true;
      }),
    [missions, filters]
  );

  const value = useMemo(
    () => ({
      missions,
      filteredMissions: filtered,
      dueIds,
      counts,
      topics,
      loading,
      error,
      offline,
      filters,
      setFilters,
      refresh,
      getMission,
      startAttempt,
      submitStep,
      completeAttempt,
      fetchMastery,
      workspace: effectiveWorkspace ?? null,
    }),
    [
      missions,
      filtered,
      dueIds,
      counts,
      topics,
      loading,
      error,
      offline,
      filters,
      refresh,
      getMission,
      startAttempt,
      submitStep,
      completeAttempt,
      fetchMastery,
      effectiveWorkspace,
    ]
  );

  return <MissionContext.Provider value={value}>{children}</MissionContext.Provider>;
}

export function useMissions() {
  const context = useContext(MissionContext);
  if (!context) throw new Error('useMissions must be used within MissionProvider');
  return context;
}
